import { useState, useMemo } from "react"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { Pencil, Trash2, RotateCcw } from "lucide-react"
import { api } from "@/app/api/client"
import { useClientTheme } from "@/app/components/ThemeContext"
import { useTableSort } from "@/app/hooks/useTableSort"
import { useIsMobile } from "@/app/hooks/useIsMobile"
import { FilterBar } from "@/app/components/FilterBar/FilterBar"
import { SearchInput } from "@/app/components/FilterBar/SearchInput"
import { MultiSelectFilter } from "@/app/components/FilterBar/MultiSelectFilter"
import { SelectFilter } from "@/app/components/FilterBar/SelectFilter"
import { SortIcon } from "@/app/components/SortIcon"
import { TableColumnHeader } from "@/app/components/TableColumnHeader"
import { TableEmptyState, EmptyStateContent } from "@/app/components/TableEmptyState"
import { DataCard } from "@/app/components/DataCard"
import { MobileSortSelect } from "@/app/components/MobileSortSelect"
import { formatNumber, decimalToPercent } from "@/app/lib/format"
import { stickyTh } from "@/app/lib/tableStyles"
import { ACQ_GREEN, DIV_RED, txTypeColor } from "@/app/lib/txColors"
import { interestTypeLabel } from "@/app/lib/interestType"
import { strSection } from "@/app/lib/str"
import { ErrorBanner, formatApiError } from "@/app/pages/UnitsPage/UnitsPage"
import type { ClientTheme } from "@/app/lib/themes"
import { TransactionRow, type Transaction } from "./TransactionRow"
import { TransactionEditModal } from "./TransactionEditModal"
import { EditedBadge } from "./EditedBadge"

interface Props {
  data: Transaction[]
  canWrite: boolean
  showDeleted: boolean
  onToggleDeleted: (v: boolean) => void
  initialSearch?: string
}

export function TransactionsTable({
  data,
  canWrite,
  showDeleted,
  onToggleDeleted,
  initialSearch,
}: Props) {
  const { theme } = useClientTheme()
  const isMobile = useIsMobile()
  const queryClient = useQueryClient()

  const [strSearch, setStrSearch] = useState(initialSearch ?? "")
  // Empty array = "All" (no constraint, every row passes). Types therefore
  // default to "All" (both shown); a concrete array restricts to those values.
  const [trSel, setTrSel] = useState<string[]>([])
  const [typeSel, setTypeSel] = useState<string[]>([])
  const [interestSel, setInterestSel] = useState<string[]>([])
  const [sectionSel, setSectionSel] = useState<string[]>([])
  // "" = All, "yes" = ORRI Res > 0, "no" = no ORRI Res.
  const [hasOrriSel, setHasOrriSel] = useState("")
  const [hoveredRow, setHoveredRow] = useState<number | null>(null)

  const [editing, setEditing] = useState<Transaction | null>(null)
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null)

  // Invalidate every read that recomputes from transaction history. The singular
  // "inventory" key matters — the STR drill-down modal keys on ["inventory", str].
  const invalidate = () => {
    for (const key of ["transactions", "inventories", "orri_inventories", "inventory"]) {
      queryClient.invalidateQueries({ queryKey: [key] })
    }
  }
  const saveMutation = useMutation({
    mutationFn: (payload: Record<string, unknown>) =>
      api.patch(`/transactions/${editing!.id}`, payload),
    onSuccess: () => {
      invalidate()
      setEditing(null)
    },
  })
  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.del(`/transactions/${id}`),
    onSuccess: () => {
      invalidate()
      setConfirmDelete(null)
    },
  })
  const restoreMutation = useMutation({
    mutationFn: (id: string) => api.post(`/transactions/${id}/restore`),
    onSuccess: () => invalidate(),
  })

  const { sortKey, sortDir, handleSort, sortRows } = useTableSort({
    rows: data,
    numericKeys: ["net_acres", "nri", "orri_reserved"],
  })

  const trOptions = useMemo(
    () => Array.from(new Set(data.map((r) => r.township_range).filter(Boolean))).sort(),
    [data]
  )
  const typeOptions = useMemo(
    () => Array.from(new Set(data.map((r) => r.type).filter(Boolean))).sort() as string[],
    [data]
  )
  const interestOptions = useMemo(
    () => Array.from(new Set(data.map((r) => r.interest_type).filter(Boolean))).sort() as string[],
    [data]
  )
  // Sections aren't in the API payload — derive them from the canonical str.
  const sectionOptions = useMemo(
    () =>
      Array.from(new Set(data.map((r) => strSection(r.str)).filter(Boolean) as string[])).sort(
        (a, b) => Number(a) - Number(b)
      ),
    [data]
  )

  const filtered = useMemo(
    () =>
      data.filter((r) => {
        const strMatch =
          !strSearch ||
          r.str.toLowerCase().includes(strSearch.toLowerCase()) ||
          r.tx_number.toLowerCase().includes(strSearch.toLowerCase())
        const trMatch = trSel.length === 0 || trSel.includes(r.township_range)
        const typeMatch = typeSel.length === 0 || typeSel.includes(r.type)
        const interestMatch =
          interestSel.length === 0 || interestSel.includes(r.interest_type ?? "")
        const sectionMatch = sectionSel.length === 0 || sectionSel.includes(strSection(r.str) ?? "")
        const hasOrriMatch =
          hasOrriSel === "" ||
          (hasOrriSel === "yes" ? Number(r.orri_reserved) > 0 : !(Number(r.orri_reserved) > 0))
        return strMatch && trMatch && typeMatch && interestMatch && sectionMatch && hasOrriMatch
      }),
    [data, strSearch, trSel, typeSel, interestSel, sectionSel, hasOrriSel]
  )

  const sorted = useMemo(() => sortRows(filtered), [filtered, sortRows])

  const hasFilters =
    !!strSearch ||
    trSel.length > 0 ||
    typeSel.length > 0 ||
    interestSel.length > 0 ||
    sectionSel.length > 0 ||
    hasOrriSel !== ""
  const clearAll = () => {
    setStrSearch("")
    setTrSel([])
    setTypeSel([])
    setInterestSel([])
    setSectionSel([])
    setHasOrriSel("")
  }

  // Footer sums exclude soft-deleted rows so they reflect the live position.
  const liveRows = sorted.filter((r) => !r.deleted_at)
  const filteredAcq = liveRows.reduce((s, r) => s + (r.type === "ACQ" ? Number(r.net_acres) : 0), 0)
  const filteredDiv = liveRows.reduce((s, r) => s + (r.type === "DIV" ? Number(r.net_acres) : 0), 0)

  const colSpan = canWrite ? 12 : 11

  return (
    <div
      className="overflow-hidden"
      style={{
        border: `1px solid ${theme.borderColor}`,
        borderRadius: "8px",
        backgroundColor: theme.bgSurface,
      }}
    >
      <FilterBar
        hasFilters={hasFilters}
        onClear={clearAll}
        resultCount={sorted.length}
        totalCount={data.length}
        entityLabel="transaction"
        theme={theme}
      >
        <SearchInput
          value={strSearch}
          onChange={setStrSearch}
          placeholder="Search STR or TX #…"
          theme={theme}
        />
        <MultiSelectFilter
          selected={sectionSel}
          onChange={setSectionSel}
          placeholder="All Sections"
          options={sectionOptions}
          width="150px"
          theme={theme}
        />
        <MultiSelectFilter
          selected={trSel}
          onChange={setTrSel}
          placeholder="All T-R"
          options={trOptions}
          theme={theme}
        />
        <MultiSelectFilter
          selected={typeSel}
          onChange={setTypeSel}
          placeholder="All Types"
          options={typeOptions}
          width="160px"
          theme={theme}
        />
        <MultiSelectFilter
          selected={interestSel}
          onChange={setInterestSel}
          placeholder="All Interest Statuses"
          options={interestOptions}
          width="180px"
          theme={theme}
        />
        <SelectFilter
          value={hasOrriSel}
          onChange={setHasOrriSel}
          placeholder="Has ORRI: All"
          options={[
            { label: "Has ORRI", value: "yes" },
            { label: "No ORRI", value: "no" },
          ]}
          width="150px"
          theme={theme}
        />
        {canWrite && (
          <label
            className="flex items-center gap-2 flex-shrink-0"
            style={{ cursor: "pointer", height: "34px" }}
          >
            <input
              type="checkbox"
              checked={showDeleted}
              onChange={(e) => onToggleDeleted(e.target.checked)}
            />
            <span
              style={{ fontFamily: "var(--font-ui)", fontSize: "12px", color: theme.textMuted }}
            >
              Show deleted
            </span>
          </label>
        )}
      </FilterBar>

      {/* An audited mutating action must not fail invisibly. */}
      {(deleteMutation.isError || restoreMutation.isError) && (
        <div className="px-3.5">
          <ErrorBanner
            theme={theme}
            message={formatApiError(deleteMutation.error ?? restoreMutation.error)}
          />
        </div>
      )}

      {/* Mobile: card reflow. Desktop: full table. */}
      {isMobile ? (
        sorted.length === 0 ? (
          <div className="px-3.5 py-10">
            <EmptyStateContent
              message="No transactions match your filters"
              onClear={clearAll}
              theme={theme}
            />
          </div>
        ) : (
          <>
            <MobileSortSelect
              options={[
                { key: "tx_number", label: "TX #" },
                { key: "transaction_date", label: "Date" },
                { key: "type", label: "Type" },
                { key: "interest_type", label: "Interest Status" },
                { key: "str", label: "STR" },
                { key: "net_acres", label: "Net Acres" },
                { key: "nri", label: "NRI" },
                { key: "orri_reserved", label: "ORRI Res" },
              ]}
              sortKey={sortKey as string | null}
              sortDir={sortDir}
              onSort={(k) => handleSort(k as keyof Transaction)}
            />
            <div className="flex flex-col gap-2.5 p-3">
              {sorted.map((tx) => {
                const isAcq = tx.type === "ACQ"
                const valueColor = txTypeColor(tx.type, Number(tx.net_acres))
                const orriNum = Number(tx.orri_reserved)
                const isDeleted = !!tx.deleted_at
                // Strike only the card content, never the action footer — the
                // Restore label must stay legible on a deleted row.
                const strike = (node: React.ReactNode) =>
                  isDeleted ? <span style={{ textDecoration: "line-through" }}>{node}</span> : node
                return (
                  <div key={tx.id} style={{ opacity: isDeleted ? 0.55 : 1 }}>
                    <DataCard
                      title={
                        <span className="inline-flex items-center gap-2">
                          {strike(tx.tx_number)}
                          {tx.app_edited_at && <EditedBadge theme={theme} />}
                        </span>
                      }
                      accessory={
                        <span
                          className="inline-block px-2 py-0.5 rounded-full"
                          style={{
                            fontFamily: "var(--font-ui)",
                            fontSize: "11px",
                            fontWeight: 500,
                            backgroundColor: `${valueColor}1A`,
                            color: valueColor,
                          }}
                        >
                          {isAcq ? "Acquisition" : tx.type === "DIV" ? "Divestiture" : tx.type}
                        </span>
                      }
                      fields={[
                        { label: "Date", value: strike(tx.transaction_date) },
                        { label: "STR", value: strike(tx.str), mono: true },
                        { label: "T-R", value: strike(tx.township_range) },
                        {
                          label: "Interest Status",
                          value: strike(
                            (() => {
                              if (!tx.interest_type) return "—"
                              const full = interestTypeLabel(tx.interest_type)
                              return full === tx.interest_type
                                ? tx.interest_type
                                : `${tx.interest_type} — ${full}`
                            })()
                          ),
                        },
                        {
                          label: "Net Acres",
                          value: strike(
                            <span style={{ color: valueColor, fontWeight: 500 }}>
                              {formatNumber(tx.net_acres, 4)}
                            </span>
                          ),
                          mono: true,
                        },
                        { label: "NRI", value: strike(formatNumber(tx.nri, 4)), mono: true },
                        {
                          label: "ORRI Res",
                          value: strike(orriNum > 0 ? decimalToPercent(orriNum, 2) : "—"),
                          mono: true,
                        },
                        {
                          label: "Counterparty",
                          full: true,
                          value: strike(tx.counterparty ?? "—"),
                        },
                        { label: "Book / Page", full: true, value: strike(tx.book_page ?? "—") },
                      ]}
                      footer={
                        canWrite ? (
                          isDeleted ? (
                            <button
                              type="button"
                              onClick={() => restoreMutation.mutate(tx.id)}
                              disabled={restoreMutation.isPending}
                              className="btn"
                              style={iconButton(theme)}
                            >
                              <RotateCcw size={13} /> Restore
                            </button>
                          ) : (
                            <>
                              <button
                                type="button"
                                onClick={() => {
                                  saveMutation.reset()
                                  setEditing(tx)
                                }}
                                className="btn"
                                style={iconButton(theme)}
                              >
                                <Pencil size={13} /> Edit
                              </button>
                              {confirmDelete === tx.id ? (
                                <>
                                  <button
                                    type="button"
                                    onClick={() => deleteMutation.mutate(tx.id)}
                                    disabled={deleteMutation.isPending}
                                    className="btn"
                                    style={{ ...iconButton(theme), color: theme.brandAccent }}
                                  >
                                    Confirm
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => setConfirmDelete(null)}
                                    className="btn"
                                    style={iconButton(theme)}
                                  >
                                    Cancel
                                  </button>
                                </>
                              ) : (
                                <button
                                  type="button"
                                  onClick={() => {
                                    setConfirmDelete(tx.id)
                                    deleteMutation.reset()
                                  }}
                                  className="btn"
                                  style={{ ...iconButton(theme), color: theme.brandAccent }}
                                >
                                  <Trash2 size={13} /> Delete
                                </button>
                              )}
                            </>
                          )
                        ) : undefined
                      }
                    />
                  </div>
                )
              })}
            </div>
          </>
        )
      ) : (
        <div className="overflow-auto" style={{ maxHeight: "70vh" }}>
          <table className="w-full border-collapse">
            <thead>
              <tr
                style={{
                  backgroundColor: theme.bgSurface2,
                  borderBottom: `1px solid ${theme.borderColor}`,
                }}
              >
                <th className="px-2 py-1 text-left" style={{ ...stickyTh(theme), width: "1px" }} />
                <th className="px-3.5 py-2 text-left" style={stickyTh(theme)}>
                  <button
                    className="flex items-center gap-1"
                    onClick={() => handleSort("transaction_date")}
                  >
                    <TableColumnHeader label="Date" theme={theme} />
                    <SortIcon
                      col="transaction_date"
                      sortKey={sortKey}
                      sortDir={sortDir}
                      theme={theme}
                    />
                  </button>
                </th>
                <th className="px-3.5 py-2 text-left" style={stickyTh(theme)}>
                  <button className="flex items-center gap-1" onClick={() => handleSort("type")}>
                    <TableColumnHeader label="Type" theme={theme} />
                    <SortIcon col="type" sortKey={sortKey} sortDir={sortDir} theme={theme} />
                  </button>
                </th>
                <th className="px-3.5 py-2 text-left" style={stickyTh(theme)}>
                  <button className="flex items-center gap-1" onClick={() => handleSort("str")}>
                    <TableColumnHeader label="STR" theme={theme} />
                    <SortIcon col="str" sortKey={sortKey} sortDir={sortDir} theme={theme} />
                  </button>
                </th>
                <th className="px-3.5 py-2 text-left" style={stickyTh(theme)}>
                  <TableColumnHeader label="T-R" theme={theme} />
                </th>
                {/* Shrink-to-content: cells only ever hold PT / HBP pills, so
                  the abbreviated header sets the column width. */}
                <th
                  className="px-3 py-3 text-left"
                  style={{ ...stickyTh(theme), width: "1%", whiteSpace: "nowrap" }}
                >
                  <button
                    className="flex items-center gap-1"
                    onClick={() => handleSort("interest_type")}
                    title="Interest Status"
                  >
                    <TableColumnHeader label="Int. Status" theme={theme} />
                    <SortIcon
                      col="interest_type"
                      sortKey={sortKey}
                      sortDir={sortDir}
                      theme={theme}
                    />
                  </button>
                </th>
                <th className="px-3.5 py-2 text-right" style={stickyTh(theme)}>
                  <button
                    className="flex items-center gap-1 ml-auto"
                    onClick={() => handleSort("net_acres")}
                  >
                    <TableColumnHeader label="Net Acres" theme={theme} />
                    <SortIcon col="net_acres" sortKey={sortKey} sortDir={sortDir} theme={theme} />
                  </button>
                </th>
                <th className="px-3.5 py-2 text-right" style={stickyTh(theme)}>
                  <button
                    className="flex items-center gap-1 ml-auto"
                    onClick={() => handleSort("nri")}
                  >
                    <TableColumnHeader label="NRI" theme={theme} />
                    <SortIcon col="nri" sortKey={sortKey} sortDir={sortDir} theme={theme} />
                  </button>
                </th>
                <th className="px-3.5 py-2 text-right" style={stickyTh(theme)}>
                  <button
                    className="flex items-center gap-1 ml-auto"
                    onClick={() => handleSort("orri_reserved")}
                  >
                    <TableColumnHeader label="ORRI Res" theme={theme} />
                    <SortIcon
                      col="orri_reserved"
                      sortKey={sortKey}
                      sortDir={sortDir}
                      theme={theme}
                    />
                  </button>
                </th>
                <th className="px-3.5 py-2 text-left" style={stickyTh(theme)}>
                  <TableColumnHeader label="Counterparty" theme={theme} />
                </th>
                <th className="px-3.5 py-2 text-left" style={stickyTh(theme)}>
                  <TableColumnHeader label="Book / Page" theme={theme} />
                </th>
                {canWrite && (
                  <th className="px-3.5 py-2 text-right" style={stickyTh(theme)}>
                    <TableColumnHeader label="Actions" theme={theme} />
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {sorted.length === 0 ? (
                <TableEmptyState
                  message="No transactions match your filters"
                  onClear={clearAll}
                  colSpan={colSpan}
                  theme={theme}
                />
              ) : (
                sorted.map((tx, index) => (
                  <TransactionRow
                    key={tx.id}
                    tx={tx}
                    index={index}
                    hoveredRow={hoveredRow}
                    onMouseEnter={() => setHoveredRow(index)}
                    onMouseLeave={() => setHoveredRow(null)}
                    theme={theme}
                    canWrite={canWrite}
                    onEdit={() => {
                      saveMutation.reset()
                      setEditing(tx)
                    }}
                    onDelete={() => {
                      setConfirmDelete(tx.id)
                      deleteMutation.reset()
                    }}
                    onRestore={() => restoreMutation.mutate(tx.id)}
                    confirmingDelete={confirmDelete === tx.id}
                    onConfirmDelete={() => deleteMutation.mutate(tx.id)}
                    onCancelDelete={() => setConfirmDelete(null)}
                    deleting={deleteMutation.isPending}
                    restoring={restoreMutation.isPending}
                  />
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      <div
        className="flex items-center justify-between gap-2 flex-wrap px-3.5 py-3 border-t"
        style={{ borderTopColor: theme.borderColor, backgroundColor: theme.bgSurface2 }}
      >
        <span style={{ fontFamily: "var(--font-ui)", fontSize: "12px", color: theme.textMuted }}>
          {hasFilters
            ? `Showing ${sorted.length} of ${data.length} transactions`
            : `${data.length} total transactions`}
        </span>
        <div className="flex items-center gap-4">
          <span
            style={{ fontFamily: "var(--font-data)", fontSize: "12px", color: theme.textMuted }}
          >
            Acq:{" "}
            <span style={{ color: ACQ_GREEN, fontWeight: 500 }}>
              {formatNumber(filteredAcq, 4)}
            </span>
          </span>
          <span
            style={{ fontFamily: "var(--font-data)", fontSize: "12px", color: theme.textMuted }}
          >
            Div:{" "}
            <span style={{ color: DIV_RED, fontWeight: 500 }}>{formatNumber(filteredDiv, 4)}</span>
          </span>
          <span
            style={{ fontFamily: "var(--font-data)", fontSize: "12px", color: theme.textMuted }}
          >
            Net:{" "}
            <span style={{ color: theme.textPrimary, fontWeight: 500 }}>
              {formatNumber(filteredAcq - filteredDiv, 4)}
            </span>
          </span>
        </div>
      </div>

      {editing && (
        <TransactionEditModal
          tx={editing}
          onSubmit={(p) => saveMutation.mutate(p)}
          onClose={() => setEditing(null)}
          saving={saveMutation.isPending}
          error={saveMutation.isError ? formatApiError(saveMutation.error) : null}
          theme={theme}
        />
      )}
    </div>
  )
}

// Inline action button used in the mobile card footers (mirrors UnitsPage).
function iconButton(theme: ClientTheme): React.CSSProperties {
  return {
    display: "flex",
    alignItems: "center",
    gap: "4px",
    fontFamily: "var(--font-ui)",
    fontSize: "12px",
    fontWeight: 500,
    padding: "5px 9px",
    borderRadius: "7px",
    backgroundColor: theme.bgSurface2,
    color: theme.textMuted,
    border: `1px solid ${theme.borderColor}`,
    cursor: "pointer",
  }
}
